import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  Organization,
  OrganizationsListResponse,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from "@/types/api";

async function fetchOrganizations(): Promise<OrganizationsListResponse> {
  return api.get<OrganizationsListResponse>("/admin/orgs");
}

async function fetchOrganization(orgId: string): Promise<Organization> {
  return api.get<Organization>(`/admin/orgs/${orgId}`);
}

async function createOrganization(
  data: CreateOrganizationRequest
): Promise<Organization> {
  return api.post<Organization>("/admin/orgs", data);
}

async function updateOrganization({
  orgId,
  data,
}: {
  orgId: string;
  data: UpdateOrganizationRequest;
}): Promise<Organization> {
  return api.patch<Organization>(`/admin/orgs/${orgId}`, data);
}

async function deleteOrganization(orgId: string): Promise<void> {
  await api.delete(`/admin/orgs/${orgId}`);
}

export function useOrganizations() {
  return useQuery({
    queryKey: ["organizations"],
    queryFn: fetchOrganizations,
  });
}

export function useOrganization(orgId: string) {
  return useQuery({
    queryKey: ["organizations", orgId],
    queryFn: () => fetchOrganization(orgId),
    enabled: !!orgId,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrganization,
    onSuccess: (data) => {
      // Invalidate both the list and the specific org
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organizations", data.id] });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}
